import { useState } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { DateRangeSelect, useHubData, fmtUSD, rangeQuery, CardError, CardEmpty } from './DateRangeSelect';

const fmtTick = (iso, groupBy) => {
  const d = new Date(`${iso}T00:00:00`);
  if (groupBy === 'month') return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function HubTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="rounded-xl border border-[#E5EAF0] bg-white p-3 text-xs shadow-lg">
      <p className="font-semibold text-[#172033]">{new Date(`${label}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
      <p className="mt-1.5 text-[#118A35]">Money in: {fmtUSD(row.money_in)}</p>
      <p className="text-[#EF4444]">Money out: {fmtUSD(row.money_out)}</p>
      <p className="text-[#2979F2]">Net cash flow: {fmtUSD(row.net)}</p>
    </div>
  );
}

export default function CashFlowChart({ wsId, range, onRangeChange }) {
  const [hidden, setHidden] = useState({});
  const url = wsId ? `/api/dashboard-hub/cash-flow?workspace_id=${wsId}&${rangeQuery(range)}` : null;
  const { data, loading, error, retry } = useHubData(url);

  const toggle = (e) => {
    const key = e.dataKey;
    setHidden((h) => ({ ...h, [key]: !h[key] }));
  };

  return (
    <section className="rounded-2xl border border-[#E5EAF0] bg-white p-5 shadow-sm" data-testid="cash-flow-card" aria-label="Cash flow overview">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-bold text-[#172033]">Cash Flow Overview</h2>
        <DateRangeSelect value={range} onChange={onRangeChange} ariaLabel="Date range for cash flow" />
      </div>
      <div className="mt-4 h-64">
        {loading ? (
          <div className="h-full w-full animate-pulse rounded-xl bg-slate-100" data-testid="cashflow-loading" />
        ) : error ? (
          <CardError onRetry={retry} />
        ) : !data?.has_data ? (
          <CardEmpty label="No cash activity in this period — try a wider range like Year to Date" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#657085' }}
                tickFormatter={(v) => fmtTick(v, data.group_by)} minTickGap={24} />
              <YAxis tick={{ fontSize: 11, fill: '#657085' }} width={56}
                tickFormatter={(v) => fmtUSD(v, { notation: 'compact' })} />
              <Tooltip content={<HubTooltip />} />
              <Legend onClick={toggle} wrapperStyle={{ fontSize: 12, cursor: 'pointer' }} />
              <Bar dataKey="money_in" name="Money In" fill="#20B94B" radius={[3, 3, 0, 0]} hide={!!hidden.money_in} maxBarSize={18} />
              <Bar dataKey="money_out" name="Money Out" fill="#EF4444" radius={[3, 3, 0, 0]} hide={!!hidden.money_out} maxBarSize={18} />
              <Line dataKey="net" name="Net Cash Flow" stroke="#2979F2" strokeWidth={2}
                dot={{ r: 2.5, fill: '#2979F2' }} hide={!!hidden.net} type="monotone" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}
